import React, { useMemo } from 'react';
import BlockContent from '@sanity/block-content-to-react';
import { sanityClient } from '@superside/sanity';
import { richTextContainerClass } from '@superside/superside-kit';
import type { SlideType } from '@superside/lego/src/components/VisualAsset/types';
import { AnimatedText } from '@superside/ui';
import { Typeform } from './Typeform';
import { SerifText } from './SerifText';
import { type TextBlockRendererProps, TextBlockRenderer } from './TextBlockRenderer';
import { ListItemRenderer } from './ListItemRenderer';
import { ListRenderer } from './ListRenderer';
import { AccentText } from './AccentText';
import { ProseWrapper } from './ProseWrapper';
import { QuoteComponent } from './QuoteComponent';
import { SuperText } from './SuperText';

const sanityConfig = sanityClient.config();

const GridBlock = React.lazy(() =>
  import('@superside/lego-components/src/blocks/GridBlock').then((module) => ({
    default: module.GridBlock
  }))
);
const AnchorPoint = React.lazy(() =>
  import('./AnchorPoint').then((module) => ({ default: module.AnchorPoint }))
);

const LinkedInComponent = React.lazy(() =>
  import('./LinkedInComponent').then((module) => ({ default: module.LinkedInComponent }))
);

const LinkNewWindowComponent = React.lazy(() =>
  import('./LinkNewWindowComponent').then((module) => ({ default: module.LinkNewWindowComponent }))
);

const ImageComponent = React.lazy(() =>
  import('./ImageComponent').then((module) => ({ default: module.ImageComponent }))
);
const SectionHeader = React.lazy(() =>
  import('./SectionHeader').then((module) => ({ default: module.SectionHeader }))
);

const CtaWithImageComponent = React.lazy(() =>
  import('./CtaWithImageComponent').then((module) => ({ default: module.CtaWithImageComponent }))
);

const BlockQuote = React.lazy(() =>
  import('./BlockQuote').then((module) => ({ default: module.BlockQuote }))
);

const GoogleSlidesComponent = React.lazy(() =>
  import('./GoogleSlidesComponent').then((module) => ({ default: module.GoogleSlidesComponent }))
);

const FlourishPostComponent = React.lazy(() =>
  import('./FlourishPostComponent').then((module) => ({ default: module.FlourishPostComponent }))
);

const TwitterPostComponent = React.lazy(() =>
  import('./TwitterPostComponent').then((module) => ({ default: module.TwitterPostComponent }))
);

const TikTokComponent = React.lazy(() =>
  import('./TikTokComponent').then((module) => ({ default: module.TikTokComponent }))
);

const InstagramComponent = React.lazy(() =>
  import('./InstagramComponent').then((module) => ({ default: module.InstagramComponent }))
);

const ImageLinkComponent = React.lazy(() =>
  import('./ImageLinkComponent').then((module) => ({ default: module.ImageLinkComponent }))
);

const SanityImage = React.lazy(() =>
  import('./SanityImage').then((module) => ({ default: module.SanityImage }))
);

const RichTextWistiaVideo = React.lazy(() =>
  import('./RichTextWistiaVideo').then((module) => ({ default: module.RichTextWistiaVideo }))
);

const ContentCheckboxes = React.lazy(() =>
  import('./components/ContentCheckboxes').then((module) => ({ default: module.ContentCheckboxes }))
);
const FacebookPostComponent = React.lazy(() =>
  import('./components/FacebookPostComponent').then((module) => ({
    default: module.FacebookPostComponent
  }))
);
const IframeComponent = React.lazy(() =>
  import('./components/IframeComponent').then((module) => ({ default: module.IframeComponent }))
);
const RichTextTableOfContents = React.lazy(() =>
  import('./components/RichTextTableOfContents').then((module) => ({
    default: module.RichTextTableOfContents
  }))
);
const RichTextExternalLink = React.lazy(() =>
  import('./components/RichTextExternalLink').then((module) => ({
    default: module.RichTextExternalLink
  }))
);
const RichTextImage = React.lazy(() =>
  import('./components/RichTextImage').then((module) => ({ default: module.RichTextImage }))
);
const RichTextBanner = React.lazy(() =>
  import('./components/RichTextBanner').then((module) => ({ default: module.RichTextBanner }))
);
const RichTextStatisticBanner = React.lazy(() =>
  import('./components/RichTextStatisticBanner').then((module) => ({
    default: module.RichTextStatisticBanner
  }))
);
const RichTextQuote = React.lazy(() =>
  import('./components/RichTextQuote').then((module) => ({ default: module.RichTextQuote }))
);
const RichTextPeopleCards = React.lazy(() =>
  import('./components/RichTextPeopleCards').then((module) => ({
    default: module.RichTextPeopleCards
  }))
);
const RichTextAssetsGrid = React.lazy(() =>
  import('./components/RichTextAssetsGrid').then((module) => ({
    default: module.RichTextAssetsGrid
  }))
);

const RichTextStatistics = React.lazy(() =>
  import('./components/RichTextStatistics').then((module) => ({
    default: module.RichTextStatistics
  }))
);
const RichTextSectionTitle = React.lazy(() =>
  import('./components/RichTextSectionTitle').then((module) => ({
    default: module.RichTextSectionTitle
  }))
);
const RichTextVisualAsset = React.lazy(() =>
  import('./components/RichTextVisualAsset').then((module) => ({
    default: module.RichTextVisualAsset
  }))
);
const RichTextSuperscript = React.lazy(() =>
  import('./components/RichTextSuperscript').then((module) => ({
    default: module.RichTextSuperscript
  }))
);
const RichTextKeyTakeaways = React.lazy(() =>
  import('./components/RichTextKeyTakeaways').then((module) => ({
    default: module.RichTextKeyTakeaways
  }))
);
const RichTextSocialShareButtons = React.lazy(() =>
  import('./components/RichTextSocialShareButtons').then((module) => ({
    default: module.RichTextSocialShareButtons
  }))
);

const RichTextGame = React.lazy(() =>
  import('./components/RichTextGame').then((module) => ({ default: module.RichTextGame }))
);
const RichTextTableComponent = React.lazy(() =>
  import('./components/RichTextTableComponent').then((module) => ({
    default: module.RichTextTableComponent
  }))
);

const RichTextDivider = React.lazy(() =>
  import('./components/RichTextDivider').then((module) => ({ default: module.RichTextDivider }))
);

const RichTextTags = React.lazy(() =>
  import('./components/RichTextTags').then((module) => ({ default: module.RichTextTags }))
);
const RichTextListBlock = React.lazy(() =>
  import('./components/RichTextListBlock').then((module) => ({ default: module.RichTextListBlock }))
);
const RichTextFaqAccordion = React.lazy(() =>
  import('./components/RichTextFaqAccordion').then((module) => ({
    default: module.RichTextFaqAccordion
  }))
);
const CustomHeading = React.lazy(() =>
  import('./CustomHeading').then((module) => ({ default: module.CustomHeading }))
);

export type SanityBlockContentProps = {
  blocks?: any;
  components?: any;
  typeSerializers?: any;
  markSerializers?: any;
  options?: any;
  showPreviewModal?: boolean;
  slides?: SlideType[];
  title?: string;
  /**  DEPREACATED: use renderNormal instead.
   *  `normal` type will be rendered as the specified Heading (added as part of RTE revamp) */
  renderNormalAsHeading?: TextBlockRendererProps['renderNormalAsHeading'];
  /** New prop: Accepts a complete element to be rendered for normal text */
  renderNormal?: React.ReactElement;
  /** Currently only passed down to TextBlockRenderer */
  className?: string;
  /** Adds the prose styles from Tailwindcss Typography */
  isLongFormat?: boolean;
};

export const defaultImageOptions = { w: 1000, fit: 'max', quality: 90 };

export const SanityBlockComponent: React.FC<SanityBlockContentProps> = (props) => {
  const {
    typeSerializers,
    markSerializers,
    components,
    showPreviewModal,
    slides,
    title,
    renderNormalAsHeading,
    renderNormal,
    className,
    blocks: unsafeBlocks,
    isLongFormat = false,
    ...rest
  } = props;

  // added as a fix for an error thrown Quote component, inside @sanity/block-content-to-react
  // TODO remove when removing the deprecated package
  const blocks = useMemo(() => {
    if (Array.isArray(unsafeBlocks)) {
      return unsafeBlocks?.map((block: any) => ({ ...block, markDefs: block.markDefs || [] }));
    }
    if (typeof unsafeBlocks === 'object') {
      return { ...unsafeBlocks, markDefs: unsafeBlocks.markDefs || [] };
    }

    return unsafeBlocks;
  }, [unsafeBlocks]);

  const serializers = useMemo(() => {
    const finalComponents = {
      ImageComponent,
      SanityBlockComponent,
      QuoteComponent,
      RichTextSuperscript,
      ...components
    };

    const types = {
      block: (props: any) => (
        <TextBlockRenderer
          {...props}
          className={className}
          renderNormalAsHeading={renderNormalAsHeading}
          renderNormal={renderNormal}
          isLongFormat={isLongFormat}
        />
      ),
      image: (props: any) => <SanityImage {...props} />,
      richTextImage: (props: any) => (
        <RichTextImage
          showPreviewModal={showPreviewModal}
          slides={slides}
          previewModalTitle={title}
          {...props}
        />
      ),
      listBlock: RichTextListBlock,
      richTextTable: RichTextTableComponent,
      iframe: IframeComponent,
      imageLink: ImageLinkComponent,
      instagramPost: InstagramComponent,
      linkedinPost: LinkedInComponent,
      tiktokPost: TikTokComponent,
      facebookPostIframe: FacebookPostComponent,
      twitterPost: TwitterPostComponent,
      flourishPost: FlourishPostComponent,
      richTextGoogleSlides: GoogleSlidesComponent,
      blockQuote: BlockQuote,
      contentCheckboxes: ContentCheckboxes,
      ctaWithImage: CtaWithImageComponent,
      richTextWistiaVideo: RichTextWistiaVideo,
      richTextFaqAccordion: RichTextFaqAccordion,
      sectionHeader: SectionHeader,
      typeform: Typeform,
      richTextTableOfContents: RichTextTableOfContents,
      richTextExternalLink: RichTextExternalLink,
      richTextBanner: RichTextBanner,
      richTextStatisticBanner: RichTextStatisticBanner,
      keyTakeawaysBanner: RichTextKeyTakeaways,
      richTextSocialShareButtons: RichTextSocialShareButtons,
      richTextQuote: RichTextQuote,
      richTextPeopleCards: RichTextPeopleCards,
      assetsGrid: RichTextAssetsGrid,
      richTextStatistics: RichTextStatistics,
      gridBlock: (props: any) => <GridBlock {...props.node} rte />,
      richTextSectionTitle: RichTextSectionTitle,
      visualAsset: (props: any) => (
        <RichTextVisualAsset
          showPreviewModal={showPreviewModal}
          slides={slides}
          previewModalTitle={title}
          {...props}
        />
      ),
      richTextGame: RichTextGame,
      richTextDivider: RichTextDivider,
      richTextTags: RichTextTags,
      ...typeSerializers
    };

    const marks = {
      link: LinkNewWindowComponent,
      anchorId: (props: any) => <AnchorPoint plain {...props} />,
      serif: SerifText,
      accent: AccentText,
      super: SuperText,
      animatedText: AnimatedText,
      customHeading: CustomHeading,
      ...markSerializers
    };

    return {
      types: enhanceComponents({ store: types, components: finalComponents, applyNotProse: true }),
      marks: enhanceComponents({ store: marks, components: finalComponents, applyNotProse: false }),
      listItem: ListItemRenderer,
      list: ListRenderer
    };
  }, [components, typeSerializers, markSerializers]);

  return (
    <ProseWrapper enableProse={isLongFormat}>
      <BlockContent
        projectId={sanityConfig.projectId}
        dataset={sanityConfig.dataset}
        imageOptions={defaultImageOptions}
        serializers={serializers}
        className={richTextContainerClass}
        blocks={blocks}
        {...rest}
      />
    </ProseWrapper>
  );
};

export const enhanceComponents = (options: {
  store: any;
  components: any;
  applyNotProse: boolean;
}) => {
  const { store, components, applyNotProse } = options;

  return Object.keys(store).reduce((acc, key) => {
    const Component = store[key];

    return {
      ...acc,
      [key]: (props: any) =>
        !applyNotProse || key === 'block' ? (
          <Component {...components} {...props} />
        ) : (
          <div className='not-prose peer peer-[.not-prose]:mt-10'>
            <Component {...components} {...props} />
          </div>
        )
    };
  }, {});
};

export default SanityBlockComponent;
